import { useState, useEffect } from "react";
import Map, { Marker, Layer, Source } from 'react-map-gl';
import { MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import 'mapbox-gl/dist/mapbox-gl.css';

interface LocationSelectorProps {
  onLocationSelect: (coordinates: { lat: number; lng: number }) => void;
  selectedLocation?: { lat: number; lng: number };
}

export const LocationSelector = ({ onLocationSelect, selectedLocation }: LocationSelectorProps) => {
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState({ lat: 54.5, lng: -2.5 }); // UK center

  useEffect(() => {
    const fetchMapboxToken = async () => {
      try {
        // Try to get token without authentication for public forms
        const { data, error } = await supabase.functions.invoke('getMapboxToken');
        
        if (error) {
          console.error('Supabase function error:', error);
          throw new Error(`Failed to fetch Mapbox token: ${error.message}`);
        }
        
        if (data?.token) {
          setMapboxToken(data.token);
          console.log('Mapbox token fetched successfully');
        } else {
          throw new Error('No Mapbox token received from server');
        }
      } catch (error) {
        console.error('Error fetching Mapbox token:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        
        // For public forms, provide fallback instructions
        toast.error('Map unavailable. Please provide coordinates manually if needed.');
        console.log('Map loading failed, users can still submit form with manual coordinates');
      }
    };

    fetchMapboxToken();
  }, []);

  const handleMapClick = (event: any) => {
    const { lng, lat } = event.lngLat;
    onLocationSelect({ lat, lng });
  };

  // Create 25m radius circle around selected location
  const createRadiusCircle = (center: { lat: number; lng: number }) => {
    const radiusInMeters = 25;
    const points = 64;
    const coords = [];
    
    for (let i = 0; i < points; i++) {
      const angle = (i / points) * 360;
      const dx = radiusInMeters * Math.cos(angle * Math.PI / 180);
      const dy = radiusInMeters * Math.sin(angle * Math.PI / 180);
      
      // Convert meters to degrees (approximate)
      const deltaLat = dy / 111000;
      const deltaLng = dx / (111000 * Math.cos(center.lat * Math.PI / 180));
      
      coords.push([center.lng + deltaLng, center.lat + deltaLat]);
    }
    coords.push(coords[0]); // Close the polygon
    
    return {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [coords]
      }
    };
  };

  if (!mapboxToken) {
    return (
      <div className="w-full h-[300px] bg-muted rounded-lg flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading map...</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label>Click on the map to select GPS coordinates *</Label>
      <div className="w-full h-[300px] rounded-lg overflow-hidden border">
        <Map
          mapboxAccessToken={mapboxToken}
          initialViewState={{
            latitude: mapCenter.lat,
            longitude: mapCenter.lng,
            zoom: 6
          }}
          style={{ width: "100%", height: "100%" }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          onClick={handleMapClick}
          cursor="crosshair"
        >
          {selectedLocation && (
            <>
              <Marker
                latitude={selectedLocation.lat}
                longitude={selectedLocation.lng}
              >
                <div className="w-6 h-6 rounded-full bg-brand-500 border-2 border-white shadow-lg flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-white" />
                </div>
              </Marker>
              
              <Source
                type="geojson"
                data={createRadiusCircle(selectedLocation)}
              >
                <Layer
                  type="fill"
                  paint={{
                    'fill-color': '#188BAF',
                    'fill-opacity': 0.2
                  }}
                />
                <Layer
                  type="line"
                  paint={{
                    'line-color': '#188BAF',
                    'line-width': 2,
                    'line-opacity': 0.8
                  }}
                />
              </Source>
            </>
          )}
        </Map>
      </div>
      {selectedLocation && (
        <div className="text-sm text-muted-foreground">
          Selected: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)} (25m radius shown)
        </div>
      )}
      <p className="text-sm text-muted-foreground">
        Click anywhere on the map to select the location where you need image access. A 25-meter radius circle will show the coverage area.
      </p>
    </div>
  );
};